import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';

import { ActionService, cleanObject, CustomFormatter, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';

import { IGutachtenStatus, IPostGutachtenStatusResponse } from 'fa-kt.types';
import { GutachtenStatiService, PostGutachtenStatusCommandService } from '../../..';

interface IFormValue {
  gutachtenStatus?: IGutachtenStatus;
}

@Component({
  selector: 'fa-kt-post-gutachten-status',
  templateUrl: './post-gutachten-status.component.html',
  styleUrls: ['./post-gutachten-status.component.css'],
})
export class PostGutachtenStatusCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostGutachtenStatusResponse> = new EventEmitter<IPostGutachtenStatusResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public gutachtenStati: GutachtenStatiService,
    public postGutachtenStatus: PostGutachtenStatusCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.GUTACHTEN.Commands.POST_GUTACHTEN_STATUS;
    this.model = sitemap.GUTACHTEN.Models.GUTACHTEN_STATUS;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      gutachtenStatus: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        beschreibung: [null, []],
        color: [null, []],
        icon: [null, []],
        order: [null, []],
      }),
    });
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.beschreibung?.valueChanges.subscribe(async (beschreibung) => {
        /* << on-beschreibung-change    */
        /*    on-beschreibung-change >> */
      }) as Subscription,
      /* << subscibe    */
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let gutachtenStatus = {
      id: null,
      name: null,
      beschreibung: null,
      order: 0,
      color: '',
      icon: '',
    };
    if (value && value.gutachtenStatus) {
      gutachtenStatus = {
        ...gutachtenStatus,
        ...cleanObject(value.gutachtenStatus),
      };
    }
    this.form.patchValue({ gutachtenStatus });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.gutachtenStatus) {
        payload.gutachtenStatus = cleanObject(payload.gutachtenStatus);
      }
      const response: IPostGutachtenStatusResponse | undefined = await this.postGutachtenStatus.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.gutachtenStatus.get('id');
  }

  get name() {
    return this.form.controls.gutachtenStatus.get('name');
  }

  get beschreibung() {
    return this.form.controls.gutachtenStatus.get('beschreibung');
  }
}
